/**
 * Typed endpoint functions for `/api/notifications/*` (docs/APP_PLAN.md
 * §8.1, §12 M4; APP_SPEC §32).
 */
import type { HttpClient } from './http-client.js';
import { buildQueryString } from './query.js';
import type { Notification, PaginationParams } from './types.js';

interface NotificationListEnvelope {
  notifications: Notification[];
}

interface UnreadCountEnvelope {
  count: number;
}

interface ClearNotificationsEnvelope {
  deletedCount: number;
}

export interface NotificationsApi {
  /** `GET /api/notifications`. Already block-filtered at both creation and read time (docs/APP_PLAN.md §9.6). */
  list(pagination?: PaginationParams): Promise<Notification[]>;
  /** `GET /api/notifications/unread-count`. Intended for polling (e.g. a navigation badge). */
  unreadCount(): Promise<number>;
  /** `POST /api/notifications/:id/read`. Throws `NOT_FOUND` for a nonexistent or already-read notification, or one that does not belong to the caller. */
  markRead(notificationId: string): Promise<void>;
  /** `POST /api/notifications/read-all`. */
  markAllRead(): Promise<void>;
  /**
   * `DELETE /api/notifications/:id`. Permanently removes one of the
   * caller's own notifications. Throws `NOT_FOUND` for a nonexistent id
   * *and* for one belonging to another user — the server never
   * distinguishes the two.
   */
  remove(notificationId: string): Promise<void>;
  /** `DELETE /api/notifications`. Clears the caller's notification history; resolves with how many rows were removed. */
  clearAll(): Promise<number>;
}

export function createNotificationsApi(http: HttpClient): NotificationsApi {
  return {
    async list(pagination = {}) {
      const { notifications } = await http.request<NotificationListEnvelope>({
        method: 'GET',
        path: `/notifications${buildQueryString({ ...pagination })}`,
      });
      return notifications;
    },

    async unreadCount() {
      const { count } = await http.request<UnreadCountEnvelope>({
        method: 'GET',
        path: '/notifications/unread-count',
      });
      return count;
    },

    async markRead(notificationId) {
      await http.requestMaybeEmpty<never>({
        method: 'POST',
        path: `/notifications/${encodeURIComponent(notificationId)}/read`,
      });
    },

    async markAllRead() {
      await http.requestMaybeEmpty<never>({
        method: 'POST',
        path: '/notifications/read-all',
      });
    },

    async remove(notificationId) {
      await http.requestMaybeEmpty<never>({
        method: 'DELETE',
        path: `/notifications/${encodeURIComponent(notificationId)}`,
      });
    },

    async clearAll() {
      const { deletedCount } = await http.request<ClearNotificationsEnvelope>({
        method: 'DELETE',
        path: '/notifications',
      });
      return deletedCount;
    },
  };
}
